import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useFavoritesContext } from '../contexts/FavoritesCountContext';
import { StartupProfile } from '../types/firestore';
import StartupProfileComponent from '../components/profile/StartupProfile';
import { prefetchPitchDeck } from '../components/profile/PitchDeckScrollViewer';
import {
  CATALOG_CATEGORY_LABELS,
  getSinceYouFollowBadgeLabel,
  isSinceYouAddedFavoritesTitle,
  type CatalogCategory,
} from '../constants/catalog-categories';
import StartupRow from '../components/StartupRow';
import StartupCard from '../components/StartupCard';
import InfiniteHorizontalScroll from '../components/InfiniteHorizontalScroll';
import { resolveStartupId } from '../utils/startupId';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';
import { startupMatchesNameSlug, startupNameSlug } from '../utils/startupNameSlug';
import favoriteHeart from '../assets/favorites_icon__red_heart__right_red.png';

const FavoritesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { startupSlug } = useParams<{ startupSlug?: string }>();
  const {
    favorites: ctxFavorites,
    favoritesReady,
    invalidateFavorites,
    markFavoritesSeen,
    setFavoritesCount,
  } = useFavoritesContext();
  const ctxFavoritesRef = useRef(ctxFavorites);
  ctxFavoritesRef.current = ctxFavorites;
  const favoritesReadyRef = useRef(favoritesReady);
  favoritesReadyRef.current = favoritesReady;

  useEffect(() => {
    if (!favoritesReady) return;
    markFavoritesSeen();
  }, [favoritesReady, ctxFavorites, markFavoritesSeen]);
  const functions = getFunctions();
  const profileCache = useRef<Map<string, StartupProfile>>(new Map());
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const matchesCacheRef = useRef<StartupProfile[] | null>(null);
  const mostPopularCacheRef = useRef<StartupProfile[] | null>(null);

  const [favoriteStartups, setFavoriteStartups] = useState<StartupProfile[]>([]);
  const [matchedStartups, setMatchedStartups] = useState<StartupProfile[]>([]);
  const [mostPopularStartups, setMostPopularStartups] = useState<StartupProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchesReady, setMatchesReady] = useState(false);
  const [mostPopularReady, setMostPopularReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStartup, setSelectedStartup] = useState<StartupProfile | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CatalogCategory>(
    CATALOG_CATEGORY_LABELS.speciallyForYou
  );

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!user || user.userType !== 'investor') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Favorites is the top of this page. Paint it before matches and Most popular.
        const favoritesResult = favoritesReadyRef.current
          ? { success: true, favorites: ctxFavoritesRef.current }
          : await (async () => {
              try {
                const getFavorites = httpsCallable(functions, 'getFavorites');
                const result = await getFavorites({});
                return result.data as any;
              } catch (err) {
                console.error('Error fetching favorites:', err);
                return { success: false, favorites: [] };
              }
            })();

        if (cancelled) return;

        if (favoritesResult.success && favoritesResult.favorites) {
          const favoritesWithDates = favoritesResult.favorites.map((startup: any) => ({
            ...startup,
            createdAt: startup.createdAt?.seconds
              ? new Date(startup.createdAt.seconds * 1000)
              : startup.createdAt instanceof Date
                ? startup.createdAt
                : new Date(),
            updatedAt: startup.updatedAt?.seconds
              ? new Date(startup.updatedAt.seconds * 1000)
              : startup.updatedAt instanceof Date
                ? startup.updatedAt
                : new Date(),
            lastUpdatedAt: startup.lastUpdatedAt?.seconds
              ? new Date(startup.lastUpdatedAt.seconds * 1000)
              : startup.lastUpdatedAt instanceof Date
                ? startup.lastUpdatedAt
                : new Date(),
          }));
          setFavoriteStartups(favoritesWithDates);
        } else {
          setFavoriteStartups([]);
        }

        setLoading(false);

        const needsFetchMatches = matchesCacheRef.current === null;
        const needsFetchMostPopular = mostPopularCacheRef.current === null;
        const matchesTask = needsFetchMatches
          ? (async () => {
              try {
                const getInvestorMatches = httpsCallable(functions, 'getInvestorMatches');
                const result = await getInvestorMatches({ userId: user.id });
                return result.data as any;
              } catch (err) {
                console.error('Error fetching matches:', err);
                return { success: false, matches: [] };
              }
            })()
          : Promise.resolve({ success: true, matches: matchesCacheRef.current! });
        const mostPopularTask = needsFetchMostPopular
          ? (async () => {
              try {
                const getMostPopularStartups = httpsCallable(functions, 'getMostPopularStartups');
                const result = await getMostPopularStartups({});
                return result.data as any;
              } catch (err) {
                console.error('Error fetching most popular startups:', err);
                return { success: false, startups: [] };
              }
            })()
          : Promise.resolve({ success: true, startups: mostPopularCacheRef.current! });

        const matchesResult = await matchesTask;
        if (cancelled) return;

        if (matchesResult.success && matchesResult.matches) {
          if (needsFetchMatches) {
            const matchesWithDates: StartupProfile[] = matchesResult.matches.map(
              (startup: any) => ({
                ...startup,
                createdAt: startup.createdAt?.seconds
                  ? new Date(startup.createdAt.seconds * 1000)
                  : startup.createdAt instanceof Date
                    ? startup.createdAt
                    : new Date(),
                updatedAt: startup.updatedAt?.seconds
                  ? new Date(startup.updatedAt.seconds * 1000)
                  : startup.updatedAt instanceof Date
                    ? startup.updatedAt
                    : new Date(),
                lastUpdatedAt: startup.lastUpdatedAt?.seconds
                  ? new Date(startup.lastUpdatedAt.seconds * 1000)
                  : startup.lastUpdatedAt instanceof Date
                    ? startup.lastUpdatedAt
                    : new Date(),
              })
            );
            matchesCacheRef.current = matchesWithDates;
            setMatchedStartups(matchesWithDates);
          } else {
            setMatchedStartups(matchesResult.matches);
          }
        } else {
          setMatchedStartups([]);
        }
        if (!cancelled) setMatchesReady(true);

        const mostPopularResult = await mostPopularTask;
        if (cancelled) return;

        if (mostPopularResult.success && mostPopularResult.startups) {
          if (needsFetchMostPopular) {
            const mostPopularWithDates: StartupProfile[] = mostPopularResult.startups.map(
              (startup: any) => ({
                ...startup,
                createdAt: startup.createdAt?.seconds
                  ? new Date(startup.createdAt.seconds * 1000)
                  : startup.createdAt instanceof Date
                    ? startup.createdAt
                    : new Date(),
                updatedAt: startup.updatedAt?.seconds
                  ? new Date(startup.updatedAt.seconds * 1000)
                  : startup.updatedAt instanceof Date
                    ? startup.updatedAt
                    : new Date(),
                lastUpdatedAt: startup.lastUpdatedAt?.seconds
                  ? new Date(startup.lastUpdatedAt.seconds * 1000)
                  : startup.lastUpdatedAt instanceof Date
                    ? startup.lastUpdatedAt
                    : new Date(),
              })
            );
            mostPopularCacheRef.current = mostPopularWithDates;
            setMostPopularStartups(mostPopularWithDates);
          } else {
            setMostPopularStartups(mostPopularResult.startups);
          }
        } else {
          setMostPopularStartups([]);
        }
        if (!cancelled) setMostPopularReady(true);
      } catch (err: any) {
        console.error('Error fetching favorites:', err);
        if (!cancelled) {
          setError(err.message || 'Failed to load favorites. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; select uses stable fetch pattern
  }, [functions, user]);

  // Keep header badge in sync when user adds or removes favorites on this page
  useEffect(() => {
    if (!loading) {
      setFavoritesCount(favoriteStartups.length);
    }
  }, [favoriteStartups.length, loading, setFavoritesCount]);

  const selectedStartupId = useMemo(
    () => (selectedStartup ? resolveStartupId(selectedStartup) : null),
    [selectedStartup]
  );

  useEffect(() => {
    const url = selectedStartup?.pitchDeckUrl?.trim();
    if (url) prefetchPitchDeck(url);
  }, [selectedStartup?.pitchDeckUrl]);

  const clearSelectedStartup = useCallback(() => {
    setSelectedStartup(null);
    if (startupSlug) {
      navigate('/favorites', { replace: true });
    }
  }, [navigate, startupSlug]);

  const handleSelectStartup = useCallback(
    (startup: StartupProfile, category?: CatalogCategory) => {
      if (category) {
        setSelectedCategory(category);
      }
      setSelectedStartup(startup);

      const slug = startupNameSlug(startup.companyName);
      if (slug && startupSlug !== slug) {
        navigate(`/favorites/${slug}`, { replace: false });
      }

      const cached = startup.userId ? profileCache.current.get(startup.userId) : undefined;
      if (cached) {
        setSelectedStartup(cached);
        return;
      }

      const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
      getStartupProfile({ userId: startup.userId })
        .then(result => {
          const data = result.data as any;
          if (data.success && data.profile) {
            const profileWithDates: StartupProfile = {
              ...data.profile,
              createdAt: data.profile.createdAt?.seconds
                ? new Date(data.profile.createdAt.seconds * 1000)
                : new Date(),
              updatedAt: data.profile.updatedAt?.seconds
                ? new Date(data.profile.updatedAt.seconds * 1000)
                : new Date(),
              lastUpdatedAt: data.profile.lastUpdatedAt?.seconds
                ? new Date(data.profile.lastUpdatedAt.seconds * 1000)
                : new Date(),
            };
            if (startup.userId) {
              profileCache.current.set(startup.userId, profileWithDates);
            }
            setSelectedStartup(prev => {
              const prevId = prev ? resolveStartupId(prev) : null;
              const nextId = resolveStartupId(profileWithDates) ?? resolveStartupId(startup);
              return prevId && nextId && prevId !== nextId ? prev : profileWithDates;
            });
          }
        })
        .catch(err => {
          console.error('Error fetching startup profile:', err);
        });
    },
    [functions, navigate, startupSlug]
  );

  // Open /favorites/:startupSlug from the URL once lists are loaded.
  useEffect(() => {
    if (loading) return;

    if (!startupSlug) {
      if (!selectedStartup && favoriteStartups.length > 0) {
        handleSelectStartup(favoriteStartups[0], CATALOG_CATEGORY_LABELS.yourFavorites);
      }
      return;
    }

    if (selectedStartup && startupMatchesNameSlug(selectedStartup, startupSlug)) {
      return;
    }

    const fromFavorites = favoriteStartups.find(s => startupMatchesNameSlug(s, startupSlug));
    if (fromFavorites) {
      handleSelectStartup(fromFavorites, CATALOG_CATEGORY_LABELS.yourFavorites);
      return;
    }
    const fromMatches = matchedStartups.find(s => startupMatchesNameSlug(s, startupSlug));
    if (fromMatches) {
      handleSelectStartup(fromMatches, CATALOG_CATEGORY_LABELS.speciallyForYou);
      return;
    }
    const fromPopular = mostPopularStartups.find(s => startupMatchesNameSlug(s, startupSlug));
    if (fromPopular) {
      handleSelectStartup(fromPopular, CATALOG_CATEGORY_LABELS.mostPopular);
    }
  }, [
    loading,
    startupSlug,
    favoriteStartups,
    matchedStartups,
    mostPopularStartups,
    selectedStartup,
    handleSelectStartup,
  ]);

  const handleFavoriteChange = useCallback(
    (startupId: string, isFavorited: boolean) => {
      if (isFavorited) {
        if (selectedStartup && selectedStartup.id === startupId) {
          setFavoriteStartups(prev => {
            if (prev.some(fav => fav.id === startupId)) {
              return prev;
            }
            return [...prev, selectedStartup];
          });
        }
      } else {
        const removedIndex = favoriteStartups.findIndex(fav => fav.id === startupId);
        const next = favoriteStartups.filter(fav => fav.id !== startupId);
        setFavoriteStartups(next);
        if (selectedStartup?.id === startupId) {
          if (next.length > 0) {
            const fallback = next[Math.min(Math.max(removedIndex, 0), next.length - 1)];
            handleSelectStartup(fallback, CATALOG_CATEGORY_LABELS.yourFavorites);
          } else {
            clearSelectedStartup();
          }
        }
      }
      invalidateFavorites();
    },
    [
      selectedStartup,
      favoriteStartups,
      invalidateFavorites,
      handleSelectStartup,
      clearSelectedStartup,
    ]
  );

  const prefetchProfile = useCallback(
    (startup: StartupProfile) => {
      const { userId } = startup;
      if (!userId || profileCache.current.has(userId)) return;
      if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = setTimeout(() => {
        if (profileCache.current.has(userId)) return;
        const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
        getStartupProfile({ userId })
          .then(result => {
            const data = result.data as any;
            if (data.success && data.profile) {
              const profileWithDates: StartupProfile = {
                ...data.profile,
                createdAt: data.profile.createdAt?.seconds
                  ? new Date(data.profile.createdAt.seconds * 1000)
                  : new Date(),
                updatedAt: data.profile.updatedAt?.seconds
                  ? new Date(data.profile.updatedAt.seconds * 1000)
                  : new Date(),
                lastUpdatedAt: data.profile.lastUpdatedAt?.seconds
                  ? new Date(data.profile.lastUpdatedAt.seconds * 1000)
                  : new Date(),
              };
              profileCache.current.set(userId, profileWithDates);
            }
          })
          .catch(() => {
            /* prefetch failure is silent */
          });
      }, 200);
    },
    [functions]
  );

  const favoriteThumbCards = useMemo(
    () =>
      favoriteStartups.map(startup => {
        const startupId = resolveStartupId(startup);
        const isSelected = Boolean(startupId && startupId === selectedStartupId);
        return (
          <div
            key={startupId ?? startup.companyName}
            className={`rounded-sm m-0.5 transition-shadow ${
              isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-[#1d1d1d]' : ''
            }`}
          >
            <StartupCard
              startup={{
                id: startupId ?? '',
                companyName: startup.companyName,
                thumbnailUrl: startup.thumbnailUrl,
                country: startup.country,
                industry: startup.industry,
                industries: startup.industries,
                businessModel: startup.businessModel,
                stage: startup.stage,
                investment: startup.investment,
                currency: startup.currency,
                selectedSDGs: startup.selectedSDGs,
              }}
              onClick={() => handleSelectStartup(startup, CATALOG_CATEGORY_LABELS.yourFavorites)}
              onMouseEnter={() => prefetchProfile(startup)}
              isFavorited
            />
          </div>
        );
      }),
    [favoriteStartups, selectedStartupId, handleSelectStartup, prefetchProfile]
  );

  const renderFavoriteThumbStrip = () => (
    <section
      aria-label={CATALOG_CATEGORY_LABELS.yourFavorites}
      className="min-w-0 shrink-0 px-2 pt-3 pb-2 sm:px-2 sm:pt-3.5 sm:pb-2.5 [&_.startup-row-scroll]:!pt-1.5 [&_.startup-row-scroll]:!pb-3"
    >
      <InfiniteHorizontalScroll
        showArrows
        contentKey={favoriteStartups.map(s => resolveStartupId(s) ?? s.companyName).join('|')}
        itemClassName="w-[calc(33.333%-0.4rem)] sm:w-[calc(25%-0.45rem)] lg:w-[calc(16.666%-0.5rem)]"
      >
        {favoriteThumbCards}
      </InfiniteHorizontalScroll>
    </section>
  );

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.userType !== 'investor') {
    return <Navigate to="/dashboard" />;
  }

  if (error && !loading) {
    return (
      <div className="min-h-screen bg-dark-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-gray-900 rounded-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-white mb-2">Error loading favorites</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasFavorites = !loading && favoriteStartups.length > 0;
  const selectedIsFavorited = Boolean(
    selectedStartupId && favoriteStartups.some(fav => resolveStartupId(fav) === selectedStartupId)
  );
  // Empty Favorites browse: open like the catalog (category badge + rows), not Your Favorites chrome.
  const browsingCatalogOnFavorites = Boolean(selectedStartup && !selectedIsFavorited);

  return (
    <div
      className={`min-h-screen flex flex-col pt-0 ${
        selectedStartup ? 'bg-[#1d1d1d]' : 'bg-[#262626]'
      } ${selectedStartup ? 'overflow-visible' : 'overflow-x-hidden'}`}
    >
      {loading ? null : (
        <>
          {selectedStartup && selectedIsFavorited ? (
            <div className="relative z-20 w-full overflow-visible pt-0 lg:pr-3">
              <div className="lg:hidden">
                {renderFavoriteThumbStrip()}
                <div
                  className="relative z-30 h-1 bg-black shadow-lg shadow-black/60"
                  aria-hidden="true"
                />
                {/* The Favorites tab hangs halfway above the card. Keep the bar above it. */}
                <div className="h-5 sm:h-6" aria-hidden="true" />
              </div>
              <StartupProfileComponent
                key={resolveStartupId(selectedStartup) ?? selectedStartup.companyName}
                profile={selectedStartup}
                compactDeck
                trackingSource="favorites"
                trackingCategory={CATALOG_CATEGORY_LABELS.yourFavorites}
                isFavorited
                onFavoriteChange={handleFavoriteChange}
                onClose={() => undefined}
                abovePitchContent={renderFavoriteThumbStrip()}
              />
            </div>
          ) : null}

          {browsingCatalogOnFavorites ? (
            <div className="relative z-20 w-full overflow-visible pt-4 md:pt-5 lg:pt-6 lg:pr-0">
              <StartupProfileComponent
                key={resolveStartupId(selectedStartup!) ?? selectedStartup!.companyName}
                profile={selectedStartup!}
                compactDeck
                flushColumnGutter
                trackingSource="catalog"
                trackingCategory={
                  isSinceYouAddedFavoritesTitle(selectedCategory)
                    ? getSinceYouFollowBadgeLabel(favoriteStartups[0]?.companyName)
                    : selectedCategory
                }
                isFavorited={false}
                onFavoriteChange={handleFavoriteChange}
                onClose={clearSelectedStartup}
              />
            </div>
          ) : null}

          {browsingCatalogOnFavorites ? (
            <div className="relative z-20 w-full" aria-hidden="true">
              <div
                className="h-0.5 w-full"
                style={{ backgroundColor: PRAIRI_SURFACES.pitchDeck }}
              />
            </div>
          ) : null}

          {!hasFavorites ? (
            <div
              className={`relative z-10 w-full mt-1 pb-1 ${
                browsingCatalogOnFavorites ? '' : '[box-shadow:0_10px_24px_6px_rgba(0,0,0,0.55)]'
              }`}
            >
              {!browsingCatalogOnFavorites ? (
                <div className="mb-10 px-4 lg:px-6">
                  <div className="rounded-xl py-12 px-6 text-center max-w-2xl mx-auto">
                    <div className="flex justify-center mb-4">
                      <span
                        className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-800 text-gray-500"
                        aria-hidden
                      >
                        <img src={favoriteHeart} alt="" className="h-8 w-8 object-contain" />
                      </span>
                    </div>
                    <p className="text-lg text-gray-300">
                      Startups will appear here after you follow them
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                      Browse the sections below and add startups you like to see them here.
                    </p>
                  </div>
                </div>
              ) : null}
              {matchesReady ? (
                <div
                  className={`pl-3 sm:pl-4 lg:pl-6 mb-8 ${
                    browsingCatalogOnFavorites ? 'mt-3 pt-0 lg:mt-12' : 'mt-6'
                  }`}
                >
                  <StartupRow
                    title={CATALOG_CATEGORY_LABELS.speciallyForYou}
                    startups={matchedStartups}
                    onSelectStartup={handleSelectStartup}
                    onPrefetchStartup={prefetchProfile}
                    hasOpenProfile={browsingCatalogOnFavorites}
                  />
                </div>
              ) : null}
              {mostPopularReady ? (
                <div className="pl-3 sm:pl-4 lg:pl-6 mb-8 mt-6">
                  <StartupRow
                    title={CATALOG_CATEGORY_LABELS.mostPopular}
                    startups={mostPopularStartups}
                    onSelectStartup={handleSelectStartup}
                    onPrefetchStartup={prefetchProfile}
                    hasOpenProfile={browsingCatalogOnFavorites}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default FavoritesPage;
